import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { Outlet, useOutletContext, Link as RouterLink, useNavigate, useLocation, useParams  } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Container, Box, Paper, Typography, TextField, Button, Alert, Stack, Link, IconButton, InputAdornment, Divider  } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";

import LanguageSwitcher from '/@/app/LanguageSwitcher';

import { makeid } from "/@/app/utils";

export const authPath = "/auth";
const authTitle='Nestarenie Expert';
const appdata={'params':{}};

const AuthContext = createContext(null);

let accessToken = localStorage.getItem("jwt");

export function getAccessToken()
{
	return accessToken;
}

export function setAccessToken(token)
{
	accessToken = token;
	localStorage.setItem("jwt", token);
}

export function clearTokens()
{
	accessToken = null;
	localStorage.removeItem("jwt");
	localStorage.removeItem("jwt_refresh");
}

export function AuthProvider()
{
const navigate = useNavigate();
const [isAuthenticated, setIsAuthenticated] = useState( !!localStorage.getItem("jwt"));

const handleAuthFailure = useCallback((message) =>
{
	clearTokens();

	setIsAuthenticated(false);

	navigate("/login", { state: { message: message ?? { text: "Сессия истекла", type: "error" } }, replace: true });
}, [navigate]);

return (
<AuthContext.Provider value={{ isAuthenticated, handleAuthFailure }}>
<Outlet/>
</AuthContext.Provider>
);
} // AuthProvider

export function useAuth()
{
	return useContext(AuthContext);
}

function fetch_auth(fu,data)
{
	const url=window.appConfig.backend_url+'/api/v2/'+fu;
	const opts={method:'POST',body:JSON.stringify(data),headers:{'Content-Type':'application/json'}};
	return fetch(url,opts);
}


export function PasswordField({name, label, password, setPassword, size })
{
const { t } = useTranslation();
const [show, setShow] = useState(false);

return (
<TextField
	size={size}
	name={name}
	type={show ? "text" : "password"}
	label={label}
	fullWidth
	value={password}
	onChange={e => setPassword(e.target.value)}
	InputProps={{
		endAdornment: (
			<InputAdornment position="end">
			<IconButton onClick={() => setShow(s => !s)} edge="end" tabIndex={-1} > {show ? <VisibilityOff /> : <Visibility />} </IconButton>
			</InputAdornment>
		),
	}}
/>
);
}


export function AuthLayout()
{
const { t } = useTranslation();
const location = useLocation();
const [message,setMessage]=useState();

useEffect(() => { setMessage(location.state?.message ?? null); }, [location.key]);

return (
    <Box sx={{height:"100dvh",display:"flex",justifyContent:"center",alignItems:"center"}}>
      <Paper elevation={4} sx={{width:360,p:3,borderRadius:2}}>
        <Stack spacing={2}>
          <Typography variant="h5" align="center" sx={{fontWeight:500}}>Nestarenie.Expert<LanguageSwitcher/></Typography>

          {message&&( <Alert severity={message.type||"error"} sx={{py:0.5}}> {t(message.text)} </Alert>)}
<Outlet context={{ setMessage }} />
        </Stack>
      </Paper>
    </Box>
);
};


function Login()
{
const { t, i18n } = useTranslation();
const navigate=useNavigate();
const formRef = useRef(null);
const [nativeSubmit, setNativeSubmit] = useState(false);
const [redir,setRedir]=useState("");
const [email,setEmail]=useState("");
const [password,setPassword]=useState("");
const { setMessage } = useOutletContext();

const handleSubmit = (event) =>
{
	if (nativeSubmit)  return;

	event.preventDefault();
	fetch_auth('login',{'email':email, 'password':password, 'lang':i18n.language})
	.then(async re => {
		const data = await re.json();
		if (!re.ok)
		{
			setMessage({'text':data.detail || "Не удалось войти",'type':'error'});
			return;
		};
		const { access, refresh } = data;
		setAccessToken(access);
		localStorage.setItem('jwt_refresh', refresh);
		setMessage({'text':"Успешный вход",'type':'success'});
		setNativeSubmit(true);
		// redirect with page reload instead of react navigation to make browser password management work
		//setTimeout(()=>{navigate('/cabinet',{replace:true})},2000)
		setRedir(window.location.origin+'/cabinet');
		setTimeout(()=>{formRef.current?.requestSubmit()},2000)
	})
	.catch(re=>{
		console.log('login error',re);
		setMessage({'text':"Ошибка входа",'type':'error'});
	});
};

function handleOAuthLogin(provider)
{
	// Open popup
	const width = 500;
	const height = 600;
	const left = (window.innerWidth - width) / 2;
	const top = (window.innerHeight - height) / 2;
	const loginSessionId = makeid(10);

	const popup = window.open(
		window.appConfig.backend_url+'/oauth/login/'+provider+'?lsid='+loginSessionId,
		"_blank",
		`width=${width},height=${height},left=${left},top=${top}`
	);

	if (!popup) return;

	// Listen for message from backend
	const listener = (event) => {
		// Make sure the origin matches
		const backendOrigin = window.appConfig.backend_url ? new URL(window.appConfig.backend_url).origin : window.location.origin;
		if (event.origin !== backendOrigin) return;
		 if (event.data.lsid !== loginSessionId) return;

		if (event.data.type === "oauth_success") {
			const { access, refresh } = event.data;
			setAccessToken(access);
			localStorage.setItem('jwt_refresh', refresh);
			popup.close();
			window.removeEventListener("message", listener);
			setMessage({'text':'Успешный вход','type':'success'});
			setTimeout(()=>{navigate('/cabinet',{replace:true})},2000)
		}

		if (event.data.type === "oauth_error") {
			setMessage({'text':event.data.error,'type':'error'});
			console.error("OAuth error", event.data.error);
			popup.close();
			window.removeEventListener("message", listener);
		}
	};

	window.addEventListener("message", listener);
}


return (
<>
<Typography variant="h4" align="center" sx={{fontWeight:500}}> {t("auth.login-title")}</Typography>
<Box component="form" ref={formRef} onSubmit={handleSubmit} autoComplete="on" method="POST" action={window.appConfig.backend_url+"/api/v2/login"}>
<Stack spacing={1.5}>
<input type="hidden" name="redir" value={redir}/>
<TextField size="small" label={t("Адрес электронной почты")} fullWidth value={email} onChange={e=>setEmail(e.target.value)} autoComplete="username" name="email"/>
<PasswordField size="small" type="password" label={t("Пароль")} fullWidth value={password} setPassword={setPassword} autoComplete="current-password" name="password"/>
<Button type="submit" variant="contained" size="large">{t("Войти")}</Button>
</Stack>
</Box>

<Typography variant="body2" align="center"> {t("Забыли пароль ?")} <Link component={RouterLink} to="../recover">{t("Восстановить")}</Link> </Typography>
<Typography variant="body2" align="center"> {t("Нет аккаунта ?")} <Link component={RouterLink} to="../register">{t("Зарегистрироваться")}</Link> </Typography>

<Divider sx={{ my: 3 }}> <Typography variant='body2' color='text.secondary'> {t('или войдите с')} </Typography> </Divider>

<Stack spacing={1}>
<Button fullWidth variant='outlined' startIcon={<img src="/images/google.svg" alt="Google" style={{ width: 20, height: 20 }} />} onClick={() => handleOAuthLogin('google')} > {t('Google')} </Button>
<Button fullWidth variant='outlined' startIcon={<img src="/images/yandex.svg" alt="Yandex" style={{ width: 20, height: 20 }} />} onClick={() => handleOAuthLogin('yandex')} > {t('Yandex')} </Button>
</Stack>

</>
);
}

function Register()
{
const { t } = useTranslation();
const navigate=useNavigate();
const [email, setEmail] = useState('');
const [password, setPassword] = useState('');
const [password2, setPassword2] = useState('');
const { setMessage } = useOutletContext();

const handleSubmit = (event) =>
{
	event.preventDefault();
	fetch_auth('register',{'email':email, 'password':password,'password2':password2})
	.then(async re => {
		const data = await re.json();
		if (re.status==422)
		{
			let m='Ошибочные параметры';
			if (data.detail[0].loc?.[1]=='email') m='Неправильный адрес';
			if (data.detail[0].loc?.[1]=='password') m='Неправильный пароль';
			if (data.detail[0].loc?.[1]=='password2') m='Пароли не совпадают';
			setMessage({'text':m,'type':'error'});
			return;
		}
		else if (!re.ok)
		{
			setMessage({'text':data.detail || "Не удалось зарегистрироваться",'type':'error'});
			return;
		};
		if (!data.verify)
		{
			setMessage({'text':"Ошибка верификации",'type':'error'});
			return;
		};
		navigate(`../verify/${data.verify}`, { replace: true });
	})
	.catch(re=>{
		console.log('register error',re);
		setMessage({'text':"Ошибка регистрации",'type':'error'});
	});
};

return (
<>
<Typography variant="h4" align="center" sx={{fontWeight:500}}> {t("auth.register-title")}</Typography>
<Box component="form" onSubmit={handleSubmit} autoComplete="on" >
<Stack spacing={1.5}>
<TextField size="small" label={t("Адрес электронной почты")} fullWidth value={email} onChange={e=>setEmail(e.target.value)} autoComplete="username" name="email"/>
<PasswordField size="small" label={t("Пароль")} fullWidth value={password} setPassword={setPassword}  name="password"/>
<PasswordField size="small" label={t("Повторите пароль")} fullWidth value={password2} setPassword={setPassword2} name="password2"/>
<Button type="submit" variant="contained" size="large">{t("Зарегистрироваться")}</Button>
</Stack>
</Box>

<Typography variant="body2" align="center"> {t("Забыли пароль ?")} <Link component={RouterLink} to="../recover">{t("Восстановить")}</Link> </Typography>
<Typography variant="body2" align="center"> {t("Уже зарегистрированы ?")} <Link component={RouterLink} to="../login">{t("Войти")}</Link> </Typography>

</>
);
};

export function VerifyView({verifyKey, onSuccess, setMessage})
{
const { t } = useTranslation();
const navigate=useNavigate();
const [code, setCode] = useState('');
const [email, setEmail] = useState('');
const [action, setAction] = useState('');
const [ cooldownTime, setCooldownTime ] =useState(null);
const [ cooldownCounter, setCooldownCounter ] =useState(null);

useEffect(() =>
{
	if (cooldownTime <= 0) return;
	const startTime=Math.floor(Date.now()/1000);

	const id = setInterval(() =>
	{
		setCooldownCounter(Math.max(cooldownTime-Math.floor(Date.now()/1000),0));
	}, 1000);

	return () => clearInterval(id);

}, [cooldownTime]);

const resendEmail= () =>
{
	if (!verifyKey) { setMessage({'text':'Ошибка подтверждения'}); return; };
	setMessage({'text':'...','type':'info'});
	fetch_auth('verify/'+verifyKey,{'resend':true})
	.then(async re=>{
		const verify=await re.json();
		setEmail(verify.email);
		setAction(verify.action);
		if (verify.error) {setMessage({'text':verify.error}); return};
		if (!verify?.cooldown) { setMessage({'text':'Ошибка подтверждения'}); return; } ;
		setCooldownTime(Math.floor(Date.now()/1000)+verify.cooldown);
		setMessage({'text':'Код отправлен','type':'info'});

	})
	.catch(re=>{
		console.log('verify error',re);
		setMessage({'text':"Ошибка подтверждения",'type':'error'});
	});

};

const checkCode = (event) =>
{
	fetch_auth('verify/'+verifyKey,{'code':code})
	.then(async re => {
		const data = await re.json();
		if (!re.ok) { setMessage({'text':data.detail || "Ошибка подтверждения",'type':'error'}); return; };
		if (data.error) { setMessage({'text':data.error,'type':'error'}); return; };
		if (!data.verified) { setMessage({'text':"Неверный код",'type':'error'}); return; };

		if (data.action=='recover')
		{
			onSuccess(data.action,verifyKey);
		}
		else if (data.action)
		{
			onSuccess(data.action);
		};
	})
	.catch(re=>{
		console.log('register error',re);
		setMessage({'text':"Ошибка подтверждения",'type':'error'});
	});
};

useEffect(() => { resendEmail(); }, []);

return (
<>
<Typography variant="h4" align="center" sx={{fontWeight:500}}> {t("auth.verify-title")}</Typography>
<Box >
<Stack spacing={1.5}>
<Typography align="center" > {t("Электронный адрес")+' '}<strong>{email}</strong></Typography>
<TextField size="small" label={t("Код подтверждения")} fullWidth value={code} onChange={e=>setCode(e.target.value)} autoComplete="off"/>
<Button  variant="contained" size="large" onClick={()=>checkCode()}>{t("Проверить")}</Button>
</Stack>
</Box>

<Typography variant="body2" align="center"> { cooldownCounter ? ( <> {t('Повторно отправить через')} {cooldownCounter} {t('time.s')} </>) : ( <> {t("Не получили код ?")}{" "} <Link component={RouterLink} onClick={resendEmail}> {t("Отправить еще раз")} </Link> </>) } </Typography>
{action=='register' && ( <Typography variant="body2" align="center"><Link component={RouterLink} to="../register" >{t("Вернуться к регистрации")}</Link> </Typography>)}
{action=='recover' && ( <Typography variant="body2" align="center"><Link component={RouterLink} to="../recover" >{t("Вернуться к восстановлению")}</Link> </Typography>)}
{action!='email_change' && action!='password_change' && ( <Typography variant="body2" align="center"><Link component={RouterLink} to="../login" >{t("Ко входу")}</Link> </Typography>)}

</>
);
};

function Verify()
{
	const { verify_key } = useParams()
	const { setMessage } = useOutletContext()
	const navigate = useNavigate()

return (
<VerifyView
	verifyKey={verify_key}
	setMessage={setMessage}
	onSuccess={(action, key) => {
		if (action === "register")
			navigate("../login", { replace: true, state: { message: { text: "Аккаунт создан", type: "success" } } })

		if (action === "recover")
			navigate("../reset/" + key, { replace: true, state: { message: { text: "Адрес подтверждён", type: "success" } } })
	}}
	/>
);
}

function Recover()
{
const { t } = useTranslation();
const navigate=useNavigate();
const [email, setEmail] = useState('');
const { setMessage } = useOutletContext();

const handleSubmit = (event) =>
{
	event.preventDefault();
	fetch_auth('recover',{'email':email})
	.then(async re => {
		const data = await re.json();
		if (re.status==422)
		{
			let m='Ошибочные параметры';
			if (data.detail[0].loc?.[1]=='email') m='Неправильный адрес';
			setMessage({'text':m,'type':'error'});
			return;
		}
		else if (!re.ok)
		{
			setMessage({'text':data.detail || "Восстановление не удалось",'type':'error'});
			return;
		};
		if (!data.verify)
		{
			setMessage({'text':"Ошибка верификации",'type':'error'});
			return;
		};
		navigate(`../verify/${data.verify}`, { replace: true });
	})
	.catch(re=>{
		console.log('recover error',re);
		setMessage({'text':"Ошибка восстановления",'type':'error'});
	});
};

return (
<>
<Typography variant="h4" align="center" sx={{fontWeight:500}}> {t("auth.recover-title")}</Typography>
<Box component="form" onSubmit={handleSubmit} autoComplete="on" >
<Stack spacing={1.5}>
<TextField size="small" label={t("Адрес электронной почты")} fullWidth value={email} onChange={e=>setEmail(e.target.value)} autoComplete="username" name="email"/>
<Button type="submit" variant="contained" size="large">{t("Восстановить")}</Button>
</Stack>
</Box>

<Typography variant="body2" align="center"><Link component={RouterLink} to="../login" >{t("Назад ко входу")}</Link> </Typography>

</>
);
};

function Reset()
{
const { t } = useTranslation();
const navigate=useNavigate();
const { reset_key } = useParams();
const [email, setEmail] = useState('');
const [password, setPassword] = useState('');
const [password2, setPassword2] = useState('');
const { setMessage } = useOutletContext();

useEffect(() =>
{
	if (!reset_key) { setMessage({'text':'Ошибка сброса'}); return; };
	fetch_auth('verify/'+reset_key)
	.then(async re=>{
		const verify=await re.json();
		setEmail(verify.email);
		if (verify.error) {setMessage({'text':verify.error}); return};
	})
	.catch(re=>{
		console.log('verify error',re);
		setMessage({'text':"Ошибка подтверждения",'type':'error'});
	});


}, []);

const handleSubmit = (event) =>
{
	event.preventDefault();
	fetch_auth('reset',{'reset_key':reset_key, 'password':password, 'password2':password2})
	.then(async re => {
		const data = await re.json();
		if (re.status==422)
		{
			let m='Ошибочные параметры';
			if (data.detail[0].loc?.[1]=='password') m='Неправильный пароль';
			if (data.detail[0].loc?.[1]=='password2') m='Пароли не совпадают';
			setMessage({'text':m,'type':'error'});
			return;
		}
		else if (!re.ok)
		{
			setMessage({'text':data.detail || "Сброс не удался",'type':'error'});
			return;
		};
		if (data.success) navigate('../login',{replace:true,state:{message:{'text':'Пароль изменён','type':'success'}}});
	})
	.catch(re=>{
		console.log('recover error',re);
		setMessage({'text':"Ошибка сброса",'type':'error'});
	});
};

return (
<>
<Typography variant="h4" align="center" sx={{fontWeight:500}}> {t("auth.reset-title")}</Typography>
<Box component="form" onSubmit={handleSubmit} autoComplete="on" >
<Stack spacing={1.5}>
<Typography align="center" > {t("Электронный адрес")+' '}<strong>{email}</strong></Typography>
<PasswordField size="small" label={t("Новый пароль")} fullWidth value={password} setPassword={setPassword}  name="password"/>
<PasswordField size="small" label={t("Повторите пароль")} fullWidth value={password2} setPassword={setPassword2} name="password2"/>
<Button type="submit" variant="contained" size="large">{t("Установить")}</Button>
</Stack>
</Box>

<Typography variant="body2" align="center"><Link component={RouterLink} to="../login" >{t("Вернуться ко входу")}</Link> </Typography>
</>
);
};

export const authRoutes = [
{ path: 'login', element: <Login />, },
{ path: 'register', element: <Register />, },
{ path: 'verify/:verify_key', element: <Verify /> },
{ path: 'recover', element: <Recover />, },
{ path: 'reset/:reset_key', element: <Reset />, },
];

export default AuthLayout;


